import { VoxelAsset, VoxelData } from '../voxelTypes';
import { PALETTE, getRandomizedColor } from '../voxelColor';

export function generateVoxelBridge(
  id: string,
  name: string,
  length = 10,
  width = 4,
  material: 'stone' | 'wood' = 'stone'
): VoxelAsset {
  const voxels: VoxelData[] = [];

  const mainColor = material === 'stone' ? PALETTE.STONE_MEDIUM : PALETTE.WOOD_OAK_MEDIUM;
  const accentColor = material === 'stone' ? PALETTE.STONE_DARK : PALETTE.WOOD_OAK_DARK;

  const halfL = Math.floor(length / 2);
  const halfW = Math.floor(width / 2);

  // Arch span along X axis
  for (let x = -halfL; x <= halfL; x += 0.5) {
    const normX = x / halfL;
    const archY = Math.round((1 - normX * normX) * 2.2 * 10) / 10;

    // Cobblestone Roadway
    for (let z = -halfW; z <= halfW; z += 0.5) {
      const isRoadEdge = Math.abs(z) >= halfW - 0.2;
      const isMoss = Math.sin(x * 2 + z) > 0.8;

      if (!isRoadEdge) {
        // Flagstone road surface
        voxels.push({
          x, y: archY + 1.0, z,
          color: isMoss ? PALETTE.LEAF_OAK_LUSH : ((Math.floor(x * 2) + Math.floor(z * 2)) % 2 === 0 ? mainColor : PALETTE.COBBLE),
          materialType: 'opaque',
          scaleX: 0.5, scaleZ: 0.5, scaleY: 0.2
        });
      }

      // Parapet Railings on sides
      if (isRoadEdge) {
        // Stone curb
        voxels.push({
          x, y: archY + 1.1, z,
          color: accentColor,
          materialType: 'opaque',
          scale: 0.5
        });
        // Balustrade coping rail
        voxels.push({
          x, y: archY + 1.6, z,
          color: PALETTE.STONE_LIGHT,
          materialType: 'opaque',
          scale: 0.45
        });
      }
    }

    // Under-arch stone support vaulting
    for (let subY = Math.max(0, archY - 1.2); subY <= archY + 0.8; subY += 0.4) {
      voxels.push({
        x, y: subY, z: -halfW,
        color: PALETTE.STONE_DARK, materialType: 'opaque', scale: 0.4
      });
      voxels.push({
        x, y: subY, z: halfW,
        color: PALETTE.STONE_DARK, materialType: 'opaque', scale: 0.4
      });
    }
  }

  // 4 Corner Carved Stone Lantern Pillars
  const pillarLocs = [
    { x: -halfL, z: -halfW },
    { x: -halfL, z: halfW },
    { x: halfL, z: -halfW },
    { x: halfL, z: halfW }
  ];

  pillarLocs.forEach(p => {
    voxels.push({ x: p.x, y: 1.2, z: p.z, color: PALETTE.STONE_DARK, materialType: 'opaque', scale: 0.6 });
    voxels.push({ x: p.x, y: 1.8, z: p.z, color: PALETTE.STONE_LIGHT, materialType: 'opaque', scale: 0.5 });
    // Glowing brass lantern cap
    voxels.push({ x: p.x, y: 2.3, z: p.z, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: 0.4 });
    voxels.push({ x: p.x, y: 2.6, z: p.z, color: PALETTE.STONE_DARK, materialType: 'opaque', scale: 0.3 });
  });

  return {
    id,
    name,
    category: 'structure',
    description: `Detailed arched ${material} bridge with lantern posts`,
    voxels,
    dimensions: { x: length, y: 5, z: width }
  };
}

export function generateVoxelFenceSegment(
  id: string,
  name: string,
  length = 8
): VoxelAsset {
  const voxels: VoxelData[] = [];

  for (let x = 0; x < length; x++) {
    const isPost = x % 3 === 0;
    if (isPost) {
      for (let py = 0; py <= 2; py++) {
        voxels.push({ x, y: py, z: 0, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque' });
      }
    } else {
      voxels.push({ x, y: 1, z: 0, color: PALETTE.WOOD_OAK_LIGHT, materialType: 'opaque' });
    }
  }

  return {
    id,
    name,
    category: 'prop',
    description: 'Wooden split-rail fence',
    voxels,
    dimensions: { x: length, y: 3, z: 1 }
  };
}

export function generateVoxelSheepPen(
  id: string,
  name: string,
  width = 16,
  depth = 12
): VoxelAsset {
  const voxels: VoxelData[] = [];

  const halfW = Math.floor(width / 2);
  const halfD = Math.floor(depth / 2);

  const woodDark = PALETTE.WOOD_OAK_DARK;
  const woodLight = PALETTE.WOOD_OAK_LIGHT;
  const woodBark = PALETTE.WOOD_PINE_BARK;

  // 1. Thin Open Post-and-Rail Fence Perimeter
  for (let x = -halfW; x <= halfW; x++) {
    for (let z = -halfD; z <= halfD; z++) {
      const isNorth = z === -halfD;
      const isSouth = z === halfD;
      const isWest = x === -halfW;
      const isEast = x === halfW;

      if (!isNorth && !isSouth && !isWest && !isEast) continue;

      // Gate opening on East side (facing the river / bridge) at z between -1 and 1
      const isGate = isEast && z >= -1 && z <= 1;

      // Posts placed at corners and every 3-4 units
      const isCorner = (x === -halfW || x === halfW) && (z === -halfD || z === halfD);
      const isPost = isCorner || ((isNorth || isSouth) ? (Math.abs(x) % 3 === 0) : (Math.abs(z) % 3 === 0));

      if (isGate) {
        // Gate posts at z = -1 and z = 1
        if (z === -1 || z === 1) {
          for (let py = 0; py <= 2; py++) {
            voxels.push({ x, y: py, z, color: woodBark, materialType: 'opaque', scaleX: 0.6, scaleZ: 0.6 });
          }
          // Small brass cap lantern on gate post
          voxels.push({ x, y: 3, z, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: 0.5 });
        }
        // Swing gate bar at y=1 with clear see-through gaps
        if (z === 0) {
          voxels.push({ x, y: 1, z, color: woodLight, materialType: 'opaque', scaleY: 0.25, scaleX: 0.2 });
        }
        continue;
      }

      if (isPost) {
        // Vertical Post (slender round post)
        for (let py = 0; py <= 2; py++) {
          voxels.push({ x, y: py, z, color: woodBark, materialType: 'opaque', scaleX: 0.65, scaleZ: 0.65 });
        }
      } else {
        // Thin horizontal wooden plank rails with clear see-through gaps between y=0.6 and y=1.6
        if (isNorth || isSouth) {
          // Plank running along X-axis (scaleX 1.15 extends plank to overlap into posts)
          voxels.push({ x, y: 1, z, color: woodLight, materialType: 'opaque', scaleX: 1.15, scaleY: 0.25, scaleZ: 0.2 });
          voxels.push({ x, y: 2, z, color: woodLight, materialType: 'opaque', scaleX: 1.15, scaleY: 0.25, scaleZ: 0.2 });
        } else {
          // Plank running along Z-axis (scaleZ 1.15 extends plank to overlap into posts)
          voxels.push({ x, y: 1, z, color: woodLight, materialType: 'opaque', scaleZ: 1.15, scaleY: 0.25, scaleX: 0.2 });
          voxels.push({ x, y: 2, z, color: woodLight, materialType: 'opaque', scaleZ: 1.15, scaleY: 0.25, scaleX: 0.2 });
        }
      }
    }
  }

  // 2. Minimal, non-bulky interior accessories
  // Small Hay Stack in back corner
  const hayX = -halfW + 2;
  const hayZ = halfD - 2;
  voxels.push({ x: hayX, y: 0, z: hayZ, color: PALETTE.WHEAT_GOLD, materialType: 'foliage', scale: 0.9 });
  voxels.push({ x: hayX + 1, y: 0, z: hayZ, color: PALETTE.THATCH_YELLOW, materialType: 'foliage', scale: 0.8 });
  voxels.push({ x: hayX, y: 1, z: hayZ, color: PALETTE.WHEAT_GOLD, materialType: 'foliage', scale: 0.7 });

  // Small Water Trough Bucket in opposite back corner
  const bucketX = -halfW + 2;
  const bucketZ = -halfD + 2;
  voxels.push({ x: bucketX, y: 0, z: bucketZ, color: woodDark, materialType: 'opaque', scale: 0.85 });
  voxels.push({ x: bucketX, y: 1, z: bucketZ, color: PALETTE.WATER_CYAN_MID, materialType: 'water', scaleY: 0.3, scaleX: 0.7, scaleZ: 0.7 });

  return {
    id,
    name,
    category: 'structure',
    description: 'Rustic open-rail gated sheep pasture with thin wooden plank fences, see-through gaps, gate lanterns, hay stack, and water bucket.',
    voxels,
    dimensions: { x: width, y: 4, z: depth }
  };
}

